// context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

// Determine API URL based on environment
const getApiUrl = () => {
  // If running in production (deployed to Cloudflare Pages)
  if (import.meta.env.PROD) {
    return 'https://taxi.delivery-7.com';
  }
  // Local development - use proxy
  return ''; // Empty string uses proxy
};

// Create axios instance
const api = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// SINGLE request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('dev_token');
    if (token && token !== 'undefined' && token !== 'null' && token !== '') {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('🚀 API Request:', config.method.toUpperCase(), config.baseURL + config.url);
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// SINGLE response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config?.url);
    return response;
  },
  async (error) => {
    console.error('❌ API Error:', error);
    
    // Handle 404 - don't logout, just reject
    if (error.response?.status === 404) {
      console.log('ℹ️ Endpoint not found (404) - ignoring, not logging out');
      return Promise.reject(error);
    }
    
    // Handle network errors
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
      console.error('🔴 Network Error: Cannot reach the server');
      toast.error('Cannot reach the server. Please check your connection.');
      return Promise.reject(error);
    }
    
    // Handle 401 - only logout if not on login page
    if (error.response?.status === 401) {
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        console.log('🔴 401 Unauthorized - logging out');
        localStorage.removeItem('dev_token');
        localStorage.removeItem('dev_user');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('dev_token');
    const storedUser = localStorage.getItem('dev_user');
    
    if (storedToken && storedToken !== 'undefined' && storedToken !== 'null' && storedToken !== '') {
      try {
        if (storedUser && storedUser !== 'undefined' && storedUser !== 'null' && storedUser !== '') {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && typeof parsedUser === 'object') {
            setToken(storedToken);
            setUser(parsedUser);
            setIsAuthenticated(true);
            api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          } else {
            localStorage.removeItem('dev_token');
            localStorage.removeItem('dev_user');
          }
        } else {
          localStorage.removeItem('dev_token');
          localStorage.removeItem('dev_user');
        }
      } catch (error) {
        console.error('AuthProvider: Error parsing stored user data:', error);
        localStorage.removeItem('dev_token');
        localStorage.removeItem('dev_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      console.log('🔐 Attempting login for:', username);
      console.log('📍 API URL:', api.defaults.baseURL || 'Using proxy');
      
      const response = await api.post('/api/auth/login', { username, password });
      
      console.log('📦 Login response:', response.data);
      
      if (response.data && response.data.success) {
        const { token, user, apiKey } = response.data;
        
        if (!user || !user.username) {
          console.error('AuthProvider: Invalid user data in response', user);
          toast.error('Invalid user data received');
          return false;
        }
        
        setToken(token);
        setUser(user);
        setIsAuthenticated(true);
        localStorage.setItem('dev_token', token);
        localStorage.setItem('dev_user', JSON.stringify(user));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        toast.success(`Welcome ${user.fullName || user.username}!`);
        return true;
      } else {
        const errorMsg = response.data?.error || 'Login failed';
        console.error('❌ Login failed:', errorMsg);
        toast.error(errorMsg);
        return false;
      }
    } catch (error) {
      console.error('❌ AuthProvider: Login error', error);
      
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        toast.error('Cannot connect to server. Please check if the worker is running.');
      } else if (error.response?.status === 401) {
        toast.error('Invalid username or password.');
      } else {
        const message = error.response?.data?.error || 'Login failed. Please try again.';
        toast.error(message);
      }
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('dev_token');
    localStorage.removeItem('dev_user');
    delete api.defaults.headers.common['Authorization'];
    toast.success('Logged out');
  };

  const register = async (userData) => {
    try {
      const registerData = {
        ...userData,
        role: 'developer',
      };
      
      const response = await api.post('/api/auth/register', registerData);
      if (response.data && response.data.success) {
        toast.success('Registration successful! Please login.');
        return true;
      } else {
        const errorMsg = response.data?.error || 'Registration failed';
        toast.error(errorMsg);
        return false;
      }
    } catch (error) {
      console.error('AuthProvider: Register error', error);
      
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        toast.error('Cannot connect to server. Please check if the worker is running.');
      } else {
        const message = error.response?.data?.error || 'Registration failed. Please try again.';
        toast.error(message);
      }
      return false;
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    register,
    api,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { api };